/** @jsxImportSource ../../i18n */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Eye, EyeOff, Menu, Search } from 'lucide-react';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { IconButton, isMac, Kbd, LogoMark } from '../ui';
import { BrokerStatus } from './Broker';
import { routeMeta } from './navConfig';

const Breadcrumbs = ({ crumbs }) => (
  <nav aria-label="Fil d'Ariane" className="hidden min-w-0 sm:block">
    <ol className="flex min-w-0 items-center gap-1.5 text-sm">
      {crumbs.map((crumb, index) => {
        const last = index === crumbs.length - 1;
        return (
          <li key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-1.5">
            {index > 0 && <ChevronRight size={14} className="flex-shrink-0 text-fg-faint" aria-hidden="true" />}
            {last && <span aria-current="page" className="truncate font-medium text-fg">{crumb.label}</span>}
            {!last && crumb.to && <Link to={crumb.to} className="truncate text-fg-subtle transition-colors hover:text-fg">{crumb.label}</Link>}
            {!last && !crumb.to && <span className="truncate text-fg-subtle">{crumb.label}</span>}
          </li>
        );
      })}
    </ol>
  </nav>
);

export const TopBar = ({ onOpenMenu, onOpenPalette }) => {
  const { pathname } = useLocation();
  const { crumbs, title } = routeMeta(pathname);
  const { hidden, toggle } = usePrivacy();
  const shortcut = isMac() ? '⌘K' : 'Ctrl+K';
  return (
    <header className="sticky top-0 z-topbar border-b border-line bg-canvas/80 backdrop-blur-md">
      <div className="mx-auto flex h-[var(--topbar-h)] max-w-[1440px] items-center gap-3 px-4 sm:px-6 lg:px-10">
        <IconButton icon={Menu} label="Ouvrir le menu" tooltip={false} onClick={onOpenMenu} className="-ml-2 lg:hidden" />
        <Link to="/dashboard" aria-label="PEA Wallet, tableau de bord" className="flex-shrink-0 rounded-control lg:hidden">
          <LogoMark size={28} />
        </Link>
        <Breadcrumbs crumbs={crumbs} />
        <p className="min-w-0 truncate text-sm font-medium text-fg sm:hidden">{title}</p>
        <div className="ml-auto flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={onOpenPalette}
            aria-label={`Rechercher (${shortcut})`}
            aria-keyshortcuts="Meta+K Control+K"
            className="hidden h-9 w-56 items-center gap-2 rounded-control border border-line bg-surface-1/80 px-3 text-sm text-fg-subtle transition-colors duration-150 hover:border-line-strong hover:text-fg-muted md:inline-flex xl:w-72"
          >
            <Search size={15} aria-hidden="true" />
            <span>Rechercher…</span>
            <span className="ml-auto flex items-center gap-1" aria-hidden="true">
              <Kbd>{isMac() ? '⌘' : 'Ctrl'}</Kbd>
              <Kbd>K</Kbd>
            </span>
          </button>
          <IconButton icon={Search} label="Rechercher" tooltip={false} onClick={onOpenPalette} className="md:hidden" />
          <IconButton
            icon={hidden ? EyeOff : Eye}
            label={hidden ? 'Afficher les montants' : 'Masquer les montants'}
            pressed={hidden}
            onClick={toggle}
          />
          <BrokerStatus className="hidden md:inline-flex" />
        </div>
      </div>
    </header>
  );
};
